import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { LOGIN, GOOGLE_AUTH, GET_CAPTCHA } from "../api/apiFunction/authServices";
import { sendVerificationEmail, sendLoginOTP, verifyLoginOTP } from "../api/apiFunction/userServices";
import { updatePageTitle } from "../utils/titleUtils";
import { LogoWithText } from "../components/Logo";
import AuthShowcase from "../components/pages/auth/AuthShowcase";
import { viewLogs } from "../utils/viewLogs";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

// Make viewLogs available globally
if (typeof window !== "undefined") {
  window.viewLogs = viewLogs;
}

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendOption, setShowResendOption] = useState(false);
  const [loginMode, setLoginMode] = useState("password"); // "password" or "otp"
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [userNotFound, setUserNotFound] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captcha, setCaptcha] = useState({ id: null, challenge: null, answer: "" });

  // Google returns ID token
  const handleGoogleCallback = async (response) => {
    try {
      logger.info("Google callback response received");

      // Send the Google token to your backend
      const authResponse = await GOOGLE_AUTH(response.credential);

      logger.info("Backend auth response", { status: authResponse?.status });

      if (authResponse.status === 200 && authResponse.data) {
        handleLoginSuccess(authResponse.data);
      } else {
        const errorMessage =
          authResponse?.data?.message || "Google authentication failed";
        setError(errorMessage);
        toast.error(errorMessage);
        setLoading(false);
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Google auth error", { error: error.message });
      const errorMessage =
        error.response?.data?.message || "Google authentication failed";
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    updatePageTitle("Login");

    // Check for debug logs if user was redirected here
    const redirectLogs = JSON.parse(localStorage.getItem('leebai_redirect_log') || '[]');
    if (redirectLogs.length > 0) {
      logger.warn('User was redirected here', { 
        redirectCount: redirectLogs.length,
        tip: 'Type viewLogs() in console to see all debug logs'
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    setError("");
    setUserNotFound(false);
  };

  // Fetch CAPTCHA challenge
  const fetchCaptcha = async () => {
    try {
      const response = await GET_CAPTCHA();
      if (response?.status === 200 && response?.data?.captcha) {
        setCaptcha({
          id: response.data.captcha.id,
          challenge: response.data.captcha.challenge,
          answer: ""
        });
        setRequiresCaptcha(true);
        logger.info("CAPTCHA fetched", { id: response.data.captcha.id });
        return true;
      } else {
        logger.error("Invalid CAPTCHA response", { response });
        toast.error("Failed to load CAPTCHA. Please try again.");
        return false;
      }
    } catch (error) {
      logger.error("Error fetching CAPTCHA", { error: error.message });
      toast.error("Failed to load CAPTCHA. Please refresh the page.");
      return false;
    }
  };

  const handleCaptchaChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    setCaptcha((prev) => ({ ...prev, answer: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate required fields
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // Validate CAPTCHA if required
    if (requiresCaptcha) {
      if (!captcha.id || !captcha.answer) {
        setError("Please complete the CAPTCHA verification");
        setLoading(false);
        return;
      }
    }

    try {
      logger.info("Sending login request", { email: formData.email });

      // Prepare login payload
      const loginPayload = {
        email: formData.email,
        password: formData.password,
      };

      // Include CAPTCHA if required
      if (requiresCaptcha && captcha.id) {
        loginPayload.captchaId = captcha.id;
        loginPayload.captchaAnswer = captcha.answer;
      }

      // Call the login API
      const response = await LOGIN(loginPayload);

      logger.info("Login response received", { status: response?.status });

      // Check for successful response (status 200)
      if (response.status === 200 && response.data) {
        // Check email verification before proceeding with login
        // Block login only if emailVerified is explicitly false (skip for admin/super admin)
        const emailVerified = response.data?.emailVerified;
        const userRole = response.data?.role;
        const isAdmin = userRole === "admin" || userRole === "super admin" || userRole === "super_admin";
        
        logger.debug("Login - Email verification check", { emailVerified, userRole, isAdmin });
        
        // Skip email verification check for admin users
        // Only block if emailVerified is explicitly false AND user is not an admin
        // Allow undefined/null for legacy users (old users before email verification was required)
        if (!isAdmin && emailVerified === false) {
          setError(
            "Please verify your email address before logging in. Check your inbox for the 6-digit verification code."
          );
          toast.error(
            "Email verification required. Please verify your email first."
          );
          setShowResendOption(true);
          setLoading(false);
          return;
        }

        // Proceed if emailVerified is true, undefined/null (legacy support), or user is admin
        // Clear CAPTCHA on successful login
        setRequiresCaptcha(false);
        setCaptcha({ id: null, challenge: null, answer: "" });
        handleLoginSuccess(response.data);
      } else {
        // Handle API error response
      // Check if CAPTCHA is required
      if (response?.data?.requiresCaptcha || response?.data?.code === 'CAPTCHA_REQUIRED') {
        setRequiresCaptcha(true);
        // Always fetch a new CAPTCHA when required
        await fetchCaptcha();
        const errorMessage = response?.data?.message || "CAPTCHA verification required";
        setError(errorMessage);
        toast.error(errorMessage);
        setLoading(false);
        return;
      } else {
          const errorMessage =
            response?.data?.message ||
            "Login failed. Please check your credentials.";
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      
      // Also store locally for admin dashboard
      if (window.addSentryEvent) {
        window.addSentryEvent({
          id: `login-error-${Date.now()}`,
          title: `Login Error: ${error.message}`,
          message: error.response?.data?.message || error.message,
          category: 'api_error',
          level: 'error',
          timestamp: new Date().toISOString(),
          user: {
            id: 'login-attempt',
            email: formData.email || 'unknown',
            username: 'login-attempt'
          },
          context: {
            api_endpoint: '/auth/login',
            method: 'POST',
            status_code: error.response?.status,
            email: formData.email,
            login_attempt: true
          },
          count: 1,
          firstSeen: new Date().toISOString(),
          lastSeen: new Date().toISOString()
        });
      }
      
      logger.error("Login error", { error: error.message });
      
      // Check if CAPTCHA is required
      if (error.response?.data?.requiresCaptcha || error.response?.data?.code === 'CAPTCHA_REQUIRED') {
        setRequiresCaptcha(true);
        // Always fetch a new CAPTCHA when required
        await fetchCaptcha();
        const errorMessage = error.response?.data?.message || "CAPTCHA verification required";
        setError(errorMessage);
        toast.error(errorMessage);
      } else if (error.response?.data?.code === 'CAPTCHA_INVALID') {
        // Invalid CAPTCHA - fetch a new one
        setCaptcha((prev) => ({ ...prev, answer: "" }));
        await fetchCaptcha();
        const errorMessage = error.response?.data?.message || "Invalid CAPTCHA. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
      } else {
        const errorMessage =
          error.response?.data?.message || "An error occurred during login";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      toast.error("Please enter your email address first");
      return;
    }

    try {
      setLoading(true);
      const response = await sendVerificationEmail(formData.email);

      if (response?.status === 200 && response?.data?.success) {
        toast.success("Verification code sent! Please check your email.");
        setShowResendOption(false);
        // Optionally redirect to verification page
        setTimeout(() => {
          navigate("/verify-email", { state: { email: formData.email } });
        }, 1500);
      } else {
        toast.error(
          response?.data?.message ||
            "Failed to send verification code. Please try again."
        );
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Error resending verification email", { error: error.message });
      toast.error("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP code input
  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value.slice(-1); // Only take last character
    setOtpCode(newOtpCode);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpCode(digits);
      setError("");
      const lastInput = document.getElementById("otp-5");
      if (lastInput) lastInput.focus();
    }
  };

  // Send OTP for login
  const handleSendOTP = async () => {
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await sendLoginOTP(formData.email);
      
      if (response?.status === 200 && response?.data?.success) {
        toast.success("Login code sent! Please check your email.");
        setOtpSent(true);
        setOtpCode(["", "", "", "", "", ""]);
      } else {
        // Check if user doesn't exist
        if (response?.status === 404 || response?.data?.code === "USER_NOT_FOUND") {
          const errorMessage = response?.data?.message || "No account found with this email. Please create an account first.";
          setError(errorMessage);
          setUserNotFound(true);
          toast.error(errorMessage);
        } else {
          const errorMessage = response?.data?.message || "Failed to send login code. Please try again.";
          setError(errorMessage);
          setUserNotFound(false);
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      console.error("Error sending login OTP:", error);
      // Check if it's a 404 error (user not found)
      if (error?.response?.status === 404 || error?.response?.data?.code === "USER_NOT_FOUND") {
        const errorMessage = error?.response?.data?.message || "No account found with this email. Please create an account first.";
        setError(errorMessage);
        setUserNotFound(true);
        toast.error(errorMessage);
      } else {
        setError("Failed to send login code. Please try again.");
        setUserNotFound(false);
        toast.error("Failed to send login code. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and login
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    const code = otpCode.join("");
    if (code.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    if (!formData.email) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verifyLoginOTP(formData.email, code);
      
      if (response?.status === 200 && response?.data) {
        handleLoginSuccess(response.data);
      } else {
        const errorMessage = response?.data?.message || "Invalid login code. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        setOtpCode(["", "", "", "", "", ""]);
        // Focus first input
        const firstInput = document.getElementById("otp-0");
        if (firstInput) firstInput.focus();
      }
    } catch (error) {
      console.error("Error verifying login OTP:", error);
      const errorMessage = error.response?.data?.message || "Failed to verify login code. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      setOtpCode(["", "", "", "", "", ""]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (userData) => {
    // CRITICAL: Check email verification FIRST - block login if email not verified (skip for admin/super admin)
    // Only block if emailVerified is explicitly false AND user is not an admin
    const emailVerified = userData?.emailVerified;
    const userRole = userData?.role;
    const isAdmin = userRole === "admin" || userRole === "super admin" || userRole === "super_admin";
    
    logger.info("Login Success", { emailVerified, userRole, isAdmin });
    
    // Skip email verification check for admin users
    if (!isAdmin && emailVerified === false) {
      setError(
        "Please verify your email address before logging in. Check your inbox for the 6-digit verification code."
      );
      toast.error(
        "Email verification required. Please verify your email first."
      );
      setShowResendOption(true);
      setLoading(false);
      return;
    }

    // Proceed if emailVerified is true, undefined (legacy support), or user is admin

    // Organization inactive
    if (userData.organization && userData.organization.status === false) {
      setError(
        "Your organization account has been deactivated. Please contact your administrator."
      );
      toast.error("Organization account is deactivated");
      setLoading(false);
      return;
    }

    // Individual user inactive
    if (userData.role === "individual" && userData.status === false) {
      setError(
        "Your account has been deactivated. Please contact support."
      );
      toast.error("Account is deactivated");
      setLoading(false);
      return;
    }

    // Employee inactive
    if (userData.role === "employee" && userData.status === false) {
      setError(
        "Your employee account has been deactivated. Please contact your administrator."
      );
      toast.error("Employee account is deactivated");
      setLoading(false);
      return;
    }

    toast.success("Login successful!");
    localStorage.setItem("user", JSON.stringify(userData));

    setTimeout(() => {
      /* -------------------------------------------
     🎯 BILLING BYPASS CHECK - Sponsored/Manual Users
    ------------------------------------------- */
      // If billing is not required (sponsored accounts), skip all subscription checks
      if (userData.isBillingRequired === false) {
        console.log('✅ Billing bypass - Direct chat access for sponsored user');
        window.location.href = "/chat";
        return;
      }

      /* -------------------------------------------
     INDIVIDUAL USER TRIAL + PLAN CHECK (same as ProtectedRoute)
    ------------------------------------------- */
      if (userData.role === "individual") {
        const trial = userData?.trial;
        const planName = userData?.plan?.name;
        const hasPaidPlan = planName && planName !== "free";

        // 1. Paid plan → always go to chat
        if (hasPaidPlan) {
          window.location.href = "/chat";
          return;
        }

        // 2. Trial pending → go to activate-trial
        if (trial?.status === "pending") {
          window.location.href = "/activate-trial";
          return;
        }

        // 3. Trial missing → go to activate-trial
        if (!trial?.startDate || !trial?.endDate) {
          window.location.href = "/activate-trial";
          return;
        }

        const now = new Date();
        const start = new Date(trial.startDate);
        const end = new Date(trial.endDate);

        // 4. Trial active → chat
        if (now >= start && now <= end) {
          window.location.href = "/chat";
          return;
        }

        // 5. Trial expired → upgrade-plan
        window.location.href = "/upgrade-plan";
        return;
      }

      /* --------------------------------
      SUPER ADMIN & ADMIN → ADMIN PANEL
    ---------------------------------- */
      if (userData.role === "super_admin" || userData.role === "super admin" || userData.role === "admin") {
        // Check if admin user has organization (org admin) or not (system admin)
        if (userData.organization) {
          // Org admin - check if they have a plan
          if (!userData.plan || userData.plan === "free") {
            window.location.href = "/upgrade-plan";
            return;
          }
          // Org admin with plan → org dashboard
          window.location.href = "/org/dashboard";
          return;
        } else {
          // System admin (no organization) → admin dashboard
          window.location.href = "/admin";
          return;
        }
      }

      /* --------------------------------
      ORG USERS: Owner / Employee
    ---------------------------------- */

      // If free plan or no plan → upgrade-plan
      if (!userData.plan || userData.plan === "free") {
        window.location.href = "/upgrade-plan";
        return;
      }

      // Employee → Chat
      if (userData.role === "employee") {
        window.location.href = "/chat";
        return;
      }

      // Owner → Dashboard
      if (userData.role === "owner") {
        window.location.href = "/org/dashboard";
        return;
      }
    }, 1000);
  };

  const handleGoogleSignIn = () => {
    logger.info("Google Sign-In button clicked");
    setLoading(true);
    setError("");

    // Check if Google Identity Services is loaded
    if (!window.google) {
      setError("Google Sign-In is not available. Please refresh the page.");
      setLoading(false);
      return;
    }

    // Initialize Google Sign-In
    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: handleGoogleCallback,
    });

    // Create a temporary hidden div to render Google button
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.top = "-9999px";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    // Render Google button in the hidden div
    window.google.accounts.id.renderButton(tempDiv, {
      theme: "outline",
      size: "large",
    });

    // Click the Google button to trigger popup
    setTimeout(() => {
      const googleButton = tempDiv.querySelector('div[role="button"]');
      if (googleButton) {
        googleButton.click();
      }
      // Clean up
      document.body.removeChild(tempDiv);
    }, 100);

    setLoading(false);
  };

  // If already logged in, redirect; also handle pending trial + pro plan
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user && user.role && user.token) {
          const currentPath = window.location.pathname;

          // Individual users → check trial and plan and allow paid
          if (user.role === "individual") {
            const status = user?.trial?.status;
            const planName = user?.plan?.name || null;

            // Only redirect if not already on the target page to prevent loops
            if (status === "progress" || status === "paid") {
              if (currentPath !== "/chat") {
                window.location.href = "/chat";
              }
            } else if (status === "pending") {
              if (currentPath !== "/activate-trial") {
                window.location.href = "/activate-trial";
              }
            } else if (status === "expired" || status === "cancelled") {
              if (currentPath !== "/upgrade-plan") {
                window.location.href = "/upgrade-plan";
              }
            } else if (planName === "pro") {
              if (currentPath !== "/chat") {
                window.location.href = "/chat";
              }
            } else {
              if (currentPath !== "/activate-trial") {
                window.location.href = "/activate-trial";
              }
            }
            return;
          }
          // Super admins go to admin
          if (user.role === "super_admin" || user.role === "super admin") {
            if (currentPath !== "/admin") {
              window.location.href = "/admin";
            }
            return;
          }
          // Employees without a valid plan go to upgrade-plan
          if (!user.plan || user.plan === "free") {
            if (currentPath !== "/upgrade-plan") {
              window.location.href = "/upgrade-plan";
            }
            return;
          }
          // Employees with plan go to chat; org/admin go to org dashboard
          const redirectPath =
            user.role === "employee" ? "/chat" : "/org/dashboard";
          if (currentPath !== redirectPath) {
            const timer = setTimeout(() => {
              window.location.href = redirectPath;
            }, 100);
            return () => clearTimeout(timer);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        logger.error("Error parsing user data", { error: error.message });
      }
    }
  }, []);

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
              <div className="leading-tight">
                <h2 className="text-xl font-semibold text-fg-50 mb-1">
                  Log in to your account and
                </h2>
                <p className="text-xl font-semibold text-fg-50">
                  continue seamlessly.
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                <p className="text-sm text-red-700">{error}</p>
                {showResendOption && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={loading}
                      className="text-sm text-ac-02 hover:text-ac-01 font-medium underline"
                    >
                      Resend Verification Code
                    </button>
                    <span className="text-sm text-red-600">or</span>
                    <Link
                      to="/verify-email"
                      state={{ email: formData.email }}
                      className="text-sm text-ac-02 hover:text-ac-01 font-medium underline"
                    >
                      Go to Verification Page
                    </Link>
                  </div>
                )}
                {userNotFound && (
                  <div className="flex items-center gap-2 mt-2">
                    <Link
                      to="/register"
                      className="text-sm text-ac-02 hover:text-ac-01 font-medium underline"
                    >
                      Create an account
                    </Link>
                    <span className="text-sm text-red-600">to get started</span>
                  </div>
                )}
              </div>
            )}

            {/* Login Mode Toggle */}
            {/* Temporarily commented out - Email Code tab */}
            <div className="flex items-center justify-center gap-4 p-2 bg-bg-60 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setLoginMode("password");
                  setOtpSent(false);
                  setOtpCode(["", "", "", "", "", ""]);
                  setError("");
                  setUserNotFound(false);
                  setRequiresCaptcha(false);
                  setCaptcha({ id: null, challenge: null, answer: "" });
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMode === "password"
                    ? "bg-ac-02 text-white dark:text-black"
                    : "text-fg-60 hover:text-fg-50"
                }`}
                disabled={loading}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setLoginMode("otp");
                  setOtpSent(false);
                  setOtpCode(["", "", "", "", "", ""]);
                  setError("");
                  setUserNotFound(false);
                  setRequiresCaptcha(false);
                  setCaptcha({ id: null, challenge: null, answer: "" });
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  loginMode === "otp"
                    ? "bg-ac-02 text-white dark:text-black"
                    : "text-fg-60 hover:text-fg-50"
                }`}
                disabled={loading}
              >
                Email Code
              </button>
            </div>
           

            {/* Form */}
            <form className="space-y-6" onSubmit={loginMode === "otp" && otpSent ? handleVerifyOTP : handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || (loginMode === "otp" && otpSent)}
                />
              </div>

              {loginMode === "password" ? (
                <>
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-fg-50 mb-2"
                    >
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        className="w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <EyeOff
                            strokeWidth={1.5}
                            className="h-5 w-5 text-fg-60 hover:text-fg-50"
                          />
                        ) : (
                          <Eye
                            strokeWidth={1.5}
                            className="h-5 w-5 text-fg-60 hover:text-fg-50"
                          />
                        )}
                      </button>
                    </div>
                    <div className="text-right mt-2">
                      <Link
                        to="/forgot-password"
                        className="text-sm text-ac-02 hover:text-ac-01"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                  </div>

                  {/* CAPTCHA Section */}
                  {requiresCaptcha && (
                    <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                      <label className="block text-sm font-medium text-fg-50 mb-2">
                        Security Verification
                      </label>
                      <div className="space-y-3">
                        {captcha.challenge ? (
                          <>
                            <div className="flex items-center gap-3">
                              <div className="flex-1 bg-bg-70 rounded-lg p-3 text-center">
                                <p className="text-fg-50 font-medium text-lg">
                                  {captcha.challenge}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={fetchCaptcha}
                                className="px-3 py-2 text-sm text-ac-02 hover:text-ac-01 font-medium disabled:opacity-50"
                                disabled={loading}
                                title="Refresh CAPTCHA"
                              >
                                ↻
                              </button>
                            </div>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={captcha.answer}
                              onChange={handleCaptchaChange}
                              placeholder="Enter answer"
                              className="w-full px-4 py-2 bg-bg-70 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                              disabled={loading}
                              autoComplete="off"
                            />
                            <p className="text-xs text-fg-60">
                              Please solve the math problem to verify you're human
                            </p>
                          </>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-sm text-fg-60 mb-2">Loading CAPTCHA...</p>
                            <button
                              type="button"
                              onClick={fetchCaptcha}
                              className="text-sm text-ac-02 hover:text-ac-01 font-medium"
                              disabled={loading}
                            >
                              Retry
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                      loading
                        ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                        : "bg-ac-02 hover:bg-ac-01"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
                  >
                    {loading ? "Signing in..." : "Sign in"}
                  </button>
                </>
              ) : (
                <>
                  {!otpSent ? (
                    <>
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={loading || !formData.email}
                        className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                          loading || !formData.email
                            ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                            : "bg-ac-02 hover:bg-ac-01"
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
                      >
                        {loading ? "Sending..." : "Send Login Code"}
                      </button>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-fg-50 mb-2">
                          Enter 6-digit code
                        </label>
                        <div className="flex gap-2 justify-center">
                          {otpCode.map((digit, index) => (
                            <input
                              key={index}
                              id={`otp-${index}`}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              onPaste={index === 0 ? handleOtpPaste : undefined}
                              className="w-12 h-12 text-center text-lg font-semibold bg-bg-60 border-0 rounded-lg text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200"
                              disabled={loading}
                              autoFocus={index === 0}
                            />
                          ))}
                        </div>
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            onClick={() => {
                              setOtpSent(false);
                              setOtpCode(["", "", "", "", "", ""]);
                            }}
                            className="text-sm text-ac-02 hover:text-ac-01"
                            disabled={loading}
                          >
                            Change email
                          </button>
                          <span className="text-sm text-fg-60 mx-2">or</span>
                          <button
                            type="button"
                            onClick={handleSendOTP}
                            className="text-sm text-ac-02 hover:text-ac-01"
                            disabled={loading}
                          >
                            Resend code
                          </button>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading || otpCode.join("").length !== 6}
                        className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                          loading || otpCode.join("").length !== 6
                            ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                            : "bg-ac-02 hover:bg-ac-01"
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
                      >
                        {loading ? "Verifying..." : "Verify & Sign in"}
                      </button>
                    </>
                  )}
                </>
              )}
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-bd-50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-bg-70 text-fg-60">
                  or continue with
                </span>
              </div>
            </div>

            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full flex items-center justify-center py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                loading
                  ? "bg-bg-30 cursor-not-allowed"
                  : "bg-bg-60 text-fg-50 hover:bg-bg-50 border border-bd-50"
              }`}
            >
              <svg
                className="w-5 h-5 mr-3"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </button>

            {/* Sign Up Link */}
            <div className="text-center space-y-3">
              <p className="text-sm text-fg-60">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="font-medium text-fg-50 hover:text-fg-40"
                >
                  Create account
                </Link>
              </p>
              <div className="pt-3 border-t border-bd-50">
                <p className="text-xs text-fg-60 mb-3">
                  Want to try it first? No account needed!
                </p>
                <Link
                  to="/chat"
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 text-sm font-medium text-fg-50 border border-bd-50 rounded-lg bg-bg-60 hover:bg-bg-50 transition-colors"
                >
                  Try Chat Free (3 messages/day)
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default Login;
